import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchMusicTracks,
  createMusicTrack,
  updateMusicTrack,
  deleteMusicTrack,
  type MusicTrack,
} from '../lib/musicService';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const MusicTracks: React.FC = () => {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Formulaire de création
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileDuration, setFileDuration] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTracks(await fetchMusicTracks());
    } catch (err) {
      alert(`Erreur de chargement: ${err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => stopPreview();
  }, [load]);

  // ─── Préécoute ────────────────────────────────────────────────────

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePreview = (track: MusicTrack) => {
    if (playingId === track.id) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(track.fileUrl);
    audio.loop = true;
    audio.play().catch(() => {});
    audioRef.current = audio;
    setPlayingId(track.id);
  };

  // ─── Création ─────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setFileDuration(0);
    if (f) {
      const url = URL.createObjectURL(f);
      const probe = new Audio(url);
      probe.addEventListener('loadedmetadata', () => {
        setFileDuration(Math.round(probe.duration || 0));
        URL.revokeObjectURL(url);
      });
      if (!title) {
        setTitle(f.name.replace(/\.[^.]+$/, ''));
      }
    }
  };

  const resetForm = () => {
    setTitle('');
    setArtist('');
    setFile(null);
    setFileDuration(0);
  };

  const handleCreate = async () => {
    if (!file || !title.trim()) return;
    setSaving(true);
    try {
      await createMusicTrack({
        title: title.trim(),
        artist: artist.trim() || undefined,
        file,
        durationSeconds: fileDuration,
      });
      setShowCreateModal(false);
      resetForm();
      await load();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setSaving(false);
    }
  };

  // ─── Actions ──────────────────────────────────────────────────────

  const handleToggleActive = async (track: MusicTrack) => {
    try {
      await updateMusicTrack(track.id, { isActive: !track.isActive });
      setTracks((prev) =>
        prev.map((t) => (t.id === track.id ? { ...t, isActive: !t.isActive } : t)),
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleDelete = async (track: MusicTrack) => {
    if (!confirm(`Supprimer "${track.title}" ? Les publications qui l'utilisent perdront leur musique.`)) return;
    try {
      if (playingId === track.id) stopPreview();
      await deleteMusicTrack(track);
      setTracks((prev) => prev.filter((t) => t.id !== track.id));
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleSortOrderChange = async (track: MusicTrack, value: string) => {
    const sortOrder = parseInt(value, 10);
    if (Number.isNaN(sortOrder)) return;
    try {
      await updateMusicTrack(track.id, { sortOrder });
      setTracks((prev) =>
        prev.map((t) => (t.id === track.id ? { ...t, sortOrder } : t)),
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Librairie musicale</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Musiques libres de droits proposées dans le composer vidéo de l'app mobile
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors"
        >
          + Nouvelle musique
        </button>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {tracks.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            Aucune musique. Ajoutez votre première piste libre de droits.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-3 w-12"></th>
                <th className="px-4 py-3">Titre</th>
                <th className="px-4 py-3">Artiste</th>
                <th className="px-4 py-3">Durée</th>
                <th className="px-4 py-3">Ordre</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map((track) => (
                <tr key={track.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => togglePreview(track)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        playingId === track.id
                          ? 'bg-[#00244F] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={playingId === track.id ? 'Arrêter' : 'Préécouter'}
                    >
                      {playingId === track.id ? '⏸' : '▶'}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{track.title}</td>
                  <td className="px-4 py-3 text-gray-600">{track.artist || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {track.durationSeconds > 0 ? formatDuration(track.durationSeconds) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      defaultValue={track.sortOrder}
                      onBlur={(e) => handleSortOrderChange(track, e.target.value)}
                      className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full border ${
                        track.isActive
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200'
                      }`}
                    >
                      {track.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => handleToggleActive(track)}
                      className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                    >
                      {track.isActive ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      onClick={() => handleDelete(track)}
                      className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal création */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => !saving && setShowCreateModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Nouvelle musique</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Uniquement des pistes libres de droits (MP3, max 15 Mo)
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fichier audio</label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:rounded-lg file:bg-gray-100 file:text-gray-700 file:text-sm hover:file:bg-gray-200"
                />
                {file && fileDuration > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Durée détectée : {formatDuration(fileDuration)}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Upbeat Energy"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Artiste (optionnel)</label>
                <input
                  type="text"
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder="Ex: Studio Libre"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !file || !title.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
              >
                {saving ? 'Envoi…' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicTracks;
